import { useCallback, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { orgUiPath } from '@/core/api';
import { useOrgId } from '@/hooks/useOrgId';
import { useCampaign } from '@/hooks/useCampaigns';
import { useCreatives } from '@/hooks/useCreatives';
import { useDismissOnOutsideClick } from '@/hooks/useDismissOnOutsideClick';
import { DROPDOWN_ITEM, DROPDOWN_PANEL } from '@/components/primitives/dropdownMenuClasses';
import { deleteCreative } from '@/services/creatives';
import { formatRelativeDate } from '@/utils/date';
import type { Creative } from '@/types/api';
import { creativeEditorHref, creativeEditorLinkState } from '@/rve/editorBackNav';
import { NewCreativeModal } from '@/components/modals/NewCreativeModal';
import AssignCreativeModal from '@/components/modals/AssignCreativeModal';
import { EditCampaignDetailsModal } from '@/components/modals/EditCampaignDetailsModal';
import { Button } from '@/components/primitives/Button';
import { Icon } from '@/components/primitives/Icon';

function inferStatus(c: Creative): 'Ready' | 'Draft' {
  return c.thumbnail_url ? 'Ready' : 'Draft';
}

const STATUS_CONFIG = {
  Ready: 'aspectly:bg-white aspectly:text-tertiary aspectly:border aspectly:border-outline-variant/40',
  Draft: 'aspectly:bg-surface-container-high aspectly:text-on-surface-variant',
};

export default function CampaignDetailPage() {
  const orgId = useOrgId();
  const { campaignId } = useParams<{ campaignId: string }>();
  const id = campaignId ? parseInt(campaignId, 10) : null;
  const [isNewCreativeOpen, setIsNewCreativeOpen] = useState(false);
  const [isAssignCreativeOpen, setIsAssignCreativeOpen] = useState(false);
  const [isCreativeMenuOpen, setIsCreativeMenuOpen] = useState(false);
  const [isEditCampaignOpen, setIsEditCampaignOpen] = useState(false);
  const creativeMenuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const closeCreativeMenu = useCallback(() => setIsCreativeMenuOpen(false), []);

  useDismissOnOutsideClick({
    isActive: isCreativeMenuOpen,
    onDismiss: closeCreativeMenu,
    containerRef: creativeMenuRef,
  });

  const campaign = useCampaign(id);
  const creatives = useCreatives({ campaign_id: id ?? undefined });

  async function handleDelete(e: React.MouseEvent, creativeId: number) {
    e.preventDefault();
    if (!confirm('Delete this creative?')) return;
    try {
      await deleteCreative(creativeId);
      creatives.refetch();
    } catch {
      creatives.refetch();
    }
  }

  if (campaign.loading) {
    return (
      <div className="aspectly:p-8 aspectly:lg:p-12 aspectly:space-y-8 aspectly:animate-pulse aspectly:max-w-5xl">
        <div className="aspectly:h-8 aspectly:w-64 aspectly:bg-surface-container-high aspectly:rounded" />
        <div className="aspectly:h-4 aspectly:w-40 aspectly:bg-surface-container aspectly:rounded" />
        <div className="aspectly:space-y-3 aspectly:mt-8">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="aspectly:h-20 aspectly:bg-surface-container-lowest aspectly:rounded-xl aspectly:border aspectly:border-outline-variant/10"
            />
          ))}
        </div>
      </div>
    );
  }

  if (campaign.error) {
    return (
      <div className="aspectly:p-8">
        <div className="aspectly:p-4 aspectly:rounded-xl aspectly:bg-error-container aspectly:text-on-error-container aspectly:text-sm">
          {campaign.error}
        </div>
        <Link
          to={orgUiPath(orgId, '/campaigns')}
          className="aspectly:mt-4 aspectly:inline-flex aspectly:items-center aspectly:gap-2 aspectly:text-primary aspectly:font-bold aspectly:text-sm aspectly:hover:underline"
        >
          <span className="material-symbols-outlined aspectly:text-lg">arrow_back</span>
          Back to Campaigns
        </Link>
      </div>
    );
  }

  const c = campaign.data;
  if (!c) return null;

  return (
    <div className="aspectly:p-8 aspectly:lg:p-12 aspectly:max-w-5xl aspectly:space-y-10">
      {/* Breadcrumb */}
      <div className="aspectly:flex aspectly:items-center aspectly:gap-2 aspectly:text-sm aspectly:text-on-surface-variant">
        <Link to={orgUiPath(orgId, '/campaigns')} className="aspectly:hover:text-primary aspectly:transition-colors">
          Campaigns
        </Link>
        <span className="material-symbols-outlined aspectly:text-sm">chevron_right</span>
        <span className="aspectly:text-on-surface aspectly:font-medium">{c.name}</span>
      </div>

      {/* Campaign header */}
      <div className="aspectly:flex aspectly:items-start aspectly:justify-between aspectly:gap-6">
        <div>
          <div className="aspectly:flex aspectly:items-center aspectly:gap-3 aspectly:mb-2">
            <div className="aspectly:w-12 aspectly:h-12 aspectly:rounded-xl aspectly:bg-primary-container aspectly:flex aspectly:items-center aspectly:justify-center">
              <span
                className="material-symbols-outlined aspectly:text-primary"
                style={{ fontVariationSettings: "'FILL' 1" }}
              >
                campaign
              </span>
            </div>
            <div>
              <h1 className="aspectly:text-3xl aspectly:font-headline aspectly:font-black aspectly:tracking-tight aspectly:text-on-surface">
                {c.name}
              </h1>
              <p className="aspectly:text-xs aspectly:text-on-surface-variant aspectly:mt-0.5">
                Updated {formatRelativeDate(c.updated_at)} · Created{' '}
                {formatRelativeDate(c.created_at)}
              </p>
            </div>
          </div>
          {c.narrative && <p className="aspectly:text-on-surface-variant aspectly:mt-4 aspectly:max-w-xl">{c.narrative}</p>}
        </div>
        <div className="aspectly:flex aspectly:items-center aspectly:gap-3 aspectly:shrink-0">
          <Button variant="outline" onClick={() => setIsEditCampaignOpen(true)}>
            Edit Details
          </Button>
          <div ref={creativeMenuRef} className="aspectly:relative aspectly:shrink-0">
            <button
              type="button"
              onClick={() => setIsCreativeMenuOpen((open) => !open)}
              className="aspectly:flex aspectly:items-center aspectly:gap-2 aspectly:bg-primary aspectly:hover:bg-primary-dim aspectly:text-on-primary aspectly:px-5 aspectly:py-2.5 aspectly:rounded-xl aspectly:font-bold aspectly:text-sm aspectly:transition-all aspectly:shadow-lg aspectly:shadow-primary/20 aspectly:cursor-pointer"
              aria-expanded={isCreativeMenuOpen}
            >
              <Icon name="add" className="aspectly:text-lg" />
              Creative
              <Icon name="expand_more" className="aspectly:text-lg" />
            </button>
            {isCreativeMenuOpen && (
              <div className={`${DROPDOWN_PANEL} aspectly:right-0 aspectly:w-48`}>
                <button
                  type="button"
                  onClick={() => {
                    closeCreativeMenu();
                    setIsAssignCreativeOpen(true);
                  }}
                  className={`${DROPDOWN_ITEM} aspectly:flex aspectly:items-center aspectly:gap-2`}
                >
                  <Icon name="link" className="aspectly:text-lg" />
                  Assign
                </button>
                <button
                  type="button"
                  onClick={() => {
                    closeCreativeMenu();
                    setIsNewCreativeOpen(true);
                  }}
                  className={`${DROPDOWN_ITEM} aspectly:flex aspectly:items-center aspectly:gap-2`}
                >
                  <Icon name="add" className="aspectly:text-lg" />
                  New
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Creatives in this campaign */}
      <section className="aspectly:space-y-4">
        <div className="aspectly:flex aspectly:items-center aspectly:justify-between aspectly:pb-3 aspectly:border-b aspectly:border-outline-variant/10">
          <h2 className="aspectly:text-xl aspectly:font-headline aspectly:font-bold aspectly:text-on-surface">Creatives</h2>
          <span className="aspectly:text-xs aspectly:text-on-surface-variant aspectly:font-medium">
            {creatives.data?.length ?? '…'} total
          </span>
        </div>

        {creatives.error && (
          <div className="aspectly:p-4 aspectly:rounded-xl aspectly:bg-error-container aspectly:text-on-error-container aspectly:text-sm">
            {creatives.error}
          </div>
        )}

        {creatives.loading && (
          <div className="aspectly:space-y-3">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="aspectly:h-20 aspectly:bg-surface-container-lowest aspectly:rounded-xl aspectly:border aspectly:border-outline-variant/10 aspectly:animate-pulse"
              />
            ))}
          </div>
        )}

        {!creatives.loading &&
          (creatives.data ?? []).map((cr) => {
            const status = inferStatus(cr);
            return (
              <Link
                key={cr.id}
                to={creativeEditorHref(orgId, cr.id!, 'campaign', id)}
                state={creativeEditorLinkState('campaign', id)}
                className="aspectly:group aspectly:flex aspectly:items-center aspectly:justify-between aspectly:p-4 aspectly:rounded-xl aspectly:bg-surface-container-lowest aspectly:border aspectly:border-surface-variant/20 aspectly:ambient-shadow aspectly:hover:-translate-y-0.5 aspectly:transition-all"
              >
                <div className="aspectly:flex aspectly:items-center aspectly:gap-5">
                  {/* Thumbnail */}
                  <div className="aspectly:w-20 aspectly:h-14 aspectly:shrink-0 aspectly:rounded-lg aspectly:overflow-hidden aspectly:bg-surface-container-low">
                    {cr.thumbnail_url ? (
                      <img
                        src={cr.thumbnail_url}
                        alt={cr.name}
                        className="aspectly:w-full aspectly:h-full aspectly:object-cover"
                      />
                    ) : (
                      <div className="aspectly:w-full aspectly:h-full aspectly:flex aspectly:items-center aspectly:justify-center aspectly:bg-linear-to-br aspectly:from-primary-container aspectly:to-surface-container-low">
                        <span className="material-symbols-outlined aspectly:text-primary/30 aspectly:text-2xl">
                          image
                        </span>
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="aspectly:font-semibold aspectly:text-on-surface aspectly:group-hover:text-primary aspectly:transition-colors">
                      {cr.name}
                    </p>
                    <p className="aspectly:text-xs aspectly:text-on-surface-variant aspectly:mt-1">
                      {cr.template_id} · Edited {formatRelativeDate(cr.updated_at)}
                    </p>
                  </div>
                </div>

                <div className="aspectly:flex aspectly:items-center aspectly:gap-3">
                  <span
                    className={`aspectly:px-3 aspectly:py-1 aspectly:rounded-md aspectly:text-[10px] aspectly:font-bold aspectly:uppercase aspectly:tracking-wider ${STATUS_CONFIG[status]}`}
                  >
                    {status}
                  </span>
                  <button
                    aria-label="Delete"
                    onClick={(e) => cr.id != null && handleDelete(e, cr.id)}
                    className="aspectly:flex aspectly:items-center aspectly:justify-center aspectly:rounded-lg aspectly:size-9 aspectly:text-on-surface-variant aspectly:hover:bg-error-container aspectly:hover:text-error aspectly:transition-colors aspectly:cursor-pointer"
                  >
                    <span className="material-symbols-outlined aspectly:text-[20px]">delete</span>
                  </button>
                </div>
              </Link>
            );
          })}

        {!creatives.loading && creatives.data?.length === 0 && (
          <div className="aspectly:py-16 aspectly:text-center">
            <span className="material-symbols-outlined aspectly:text-5xl aspectly:text-primary/30">auto_awesome</span>
            <p className="aspectly:mt-4 aspectly:text-on-surface-variant">No creatives in this campaign yet.</p>
            <div className="aspectly:mt-4 aspectly:flex aspectly:flex-wrap aspectly:items-center aspectly:justify-center aspectly:gap-3">
              <button
                type="button"
                onClick={() => setIsNewCreativeOpen(true)}
                className="aspectly:inline-flex aspectly:items-center aspectly:gap-2 aspectly:bg-primary aspectly:text-on-primary aspectly:px-5 aspectly:py-2.5 aspectly:rounded-xl aspectly:font-bold aspectly:text-sm aspectly:shadow-lg aspectly:shadow-primary/20 aspectly:hover:bg-primary-dim aspectly:transition-all aspectly:cursor-pointer"
              >
                <Icon name="add" className="aspectly:text-lg" />
                New
              </button>
              <button
                type="button"
                onClick={() => setIsAssignCreativeOpen(true)}
                className="aspectly:inline-flex aspectly:items-center aspectly:gap-2 aspectly:bg-surface-container-lowest aspectly:text-primary aspectly:px-5 aspectly:py-2.5 aspectly:rounded-xl aspectly:font-bold aspectly:text-sm aspectly:border aspectly:border-outline-variant/30 aspectly:hover:bg-primary-container/30 aspectly:transition-all aspectly:cursor-pointer"
              >
                <Icon name="link" className="aspectly:text-lg" />
                Assign
              </button>
            </div>
          </div>
        )}
      </section>

      <NewCreativeModal
        isOpen={isNewCreativeOpen}
        onClose={() => setIsNewCreativeOpen(false)}
        campaignId={id ?? undefined}
        onCreated={(created) => {
          if (created.id == null) return;
          navigate(creativeEditorHref(orgId, created.id, 'campaign', id), {
            state: creativeEditorLinkState('campaign', id),
          });
        }}
      />
      <AssignCreativeModal
        isOpen={isAssignCreativeOpen}
        onClose={() => setIsAssignCreativeOpen(false)}
        campaignId={id}
        campaignName={c.name}
        onAssigned={() => creatives.refetch()}
      />
      <EditCampaignDetailsModal
        isOpen={isEditCampaignOpen}
        onClose={() => setIsEditCampaignOpen(false)}
        campaign={c}
        onSaved={() => {
          campaign.refetch();
          setIsEditCampaignOpen(false);
        }}
      />
    </div>
  );
}
